import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown, Check } from "lucide-react";
import { SUPPORTED_LANGS } from "@/i18n";
import { useSwitchLanguage } from "@/i18n/LanguageSync";

/**
 * Globe-icon dropdown used in both the navbar and the footer.
 *
 * Props:
 *   variant: "compact" (icon only, navbar) | "labeled" (icon + text, footer)
 */
export default function LanguageSwitcher({ variant = "compact" }) {
  const { i18n, t } = useTranslation();
  const switchLang = useSwitchLanguage();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const current = SUPPORTED_LANGS.find((l) => l.code === i18n.language) || SUPPORTED_LANGS[0];

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        !btnRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const isCompact = variant === "compact";

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="language-switcher-toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("language.chooseLanguage")}
        className={
          isCompact
            ? "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-slate-600 hover:text-[#0A1128] hover:bg-slate-100 transition-colors"
            : "inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-[#0A1128] hover:border-[#0A1128] transition-colors"
        }
      >
        <Globe className={isCompact ? "h-4 w-4" : "h-4 w-4"} />
        <span className={isCompact ? "text-[12.5px] font-medium uppercase" : ""}>
          {isCompact ? current.code.toUpperCase() : current.native}
        </span>
        <ChevronDown className="h-3.5 w-3.5 opacity-60" />
      </button>

      {open && (
        <div
          ref={menuRef}
          data-testid="language-switcher-menu"
          role="menu"
          className="absolute right-0 top-full mt-2 z-50 min-w-[180px] rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden"
        >
          <p className="px-3.5 pt-3 pb-1.5 text-[10.5px] uppercase tracking-[0.14em] text-slate-500">
            {t("language.chooseLanguage")}
          </p>
          <ul className="py-1">
            {SUPPORTED_LANGS.map((l) => {
              const active = l.code === current.code;
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      switchLang(l.code);
                      setOpen(false);
                    }}
                    data-testid={`language-switcher-option-${l.code}`}
                    className={`w-full flex items-center justify-between gap-3 px-3.5 py-2 text-[13.5px] transition-colors ${
                      active
                        ? "bg-[#F5F1EB] text-[#0A1128] font-medium"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="font-medium">{l.native}</span>
                      <span className="text-[11px] uppercase tracking-wide text-slate-400">
                        {l.code}
                      </span>
                    </span>
                    {active && <Check className="h-4 w-4 text-[#00A373]" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
